// path: frontend/src/components/Requests.jsx
/**
 * Раздел «Запросы и заявки». RBAC:
 *   - capabilities.canDecideChangeRequests — вкладки change-requests;
 *   - capabilities.editRoles — вкладка «Заявки на регистрацию»;
 *   - delegation.permissions.canDecide / canManage — действия по
 *     замещению (сервер: to_id и admin/director/from_id).
 */
import { useCallback, useMemo, useState } from "react";
import { useToast } from "../context/ToastContext";
import { useConfirm } from "../context/ConfirmContext";
import { useAuth } from "../hooks/useAuth";
import { useDataHelpers } from "../hooks/useDataHelpers";
import { CHANGE_KINDS, CHANGE_KIND_LIST } from "../utils/changeKinds";
import {
  ASSIGNMENT_APPROVAL_STATUSES,
  ASSIGNMENT_TARGET_KINDS,
} from "../utils/assignmentApproval";
import { substitutionKindLabel, ROLES } from "../utils/constants";
import { fmtDMY, TODAY } from "../utils/date";
import { RejectionReasonModal } from "./Modals/RejectionReasonModal";
import { ReassignApprovalModal } from "./Modals/ReassignApprovalModal";
import {
  useRegistrationsQuery,
  useDecideRegistrationMutation,
} from "../api/queries/registrations";
import {
  useChangeRequestsQuery,
  useDecideChangeRequestMutation,
} from "../api/queries/change-requests";
import {
  useRoleDelegationsQuery,
  useDecideRoleDelegationMutation,
  useRevokeRoleDelegationMutation,
  useActivateRoleDelegationMutation,
} from "../api/queries/role-delegations";
import {
  useAssignmentApprovalsQuery,
  useDecideAssignmentApprovalMutation,
} from "../api/queries/assignment-approvals";

export function Requests({ db, initialTab = "hours" }) {
  const { capabilities } = useAuth();
  const { showToast } = useToast();
  const { confirm } = useConfirm();
  const { empName } = useDataHelpers(db);

  const { data: registrations = [] } = useRegistrationsQuery();
  const { data: changeRequests = [] } = useChangeRequestsQuery();
  const { data: roleDelegations = [] } = useRoleDelegationsQuery();
  const { data: assignmentApprovals = [] } = useAssignmentApprovalsQuery();

  const decideRegMutation = useDecideRegistrationMutation();
  const decideChangeReqMutation = useDecideChangeRequestMutation();
  const decideRDMutation = useDecideRoleDelegationMutation();
  const revokeRDMutation = useRevokeRoleDelegationMutation();
  const activateRDMutation = useActivateRoleDelegationMutation();
  const decideAssignmentMutation = useDecideAssignmentApprovalMutation();

  const [tab, setTab] = useState(initialTab);
  const [rejecting, setRejecting] = useState(null);
  const [reassignFor, setReassignFor] = useState(null);

  const canDecideChange = Boolean(capabilities.canDecideChangeRequests);
  const isAdmin = Boolean(capabilities.editRoles);

  const tabs = useMemo(() => {
    const list = [];
    if (canDecideChange)
      CHANGE_KIND_LIST.forEach((k) => list.push([k.id, k.label]));
    list.push(["assignments", "Согласование назначений"]);
    list.push(["rd", "Передача ролей и замещения"]);
    if (isAdmin) list.push(["reg", "Заявки на регистрацию"]);
    return list;
  }, [canDecideChange, isAdmin]);

  const tasksById = useMemo(
    () => new Map(db.tasks.map((t) => [t.id, t])),
    [db.tasks],
  );
  const projectsById = useMemo(
    () => new Map(db.projects.map((p) => [p.id, p])),
    [db.projects],
  );

  const targetTitleOf = useCallback(
    (r) => {
      const fallback = r.targetTitle || "(удалено)";
      if (r.targetType === "task")
        return tasksById.get(r.targetId)?.title || fallback;
      return projectsById.get(r.targetId)?.name || fallback;
    },
    [tasksById, projectsById],
  );

  const decideChange = useCallback(
    async (r, ok, reason = null) => {
      try {
        await decideChangeReqMutation.mutateAsync({
          id: r.id,
          approved: ok,
          reason,
        });
      } catch (err) {
        showToast(err.message || "Не удалось применить запрос", "error");
      }
    },
    [decideChangeReqMutation, showToast],
  );

  const decideRD = useCallback(
    async (r, ok, reason = null) => {
      try {
        const payload = { id: r.id, approved: ok };
        if (reason !== null) payload.reason = reason;
        await decideRDMutation.mutateAsync(payload);
      } catch (err) {
        showToast(
          err.message || "Не удалось применить решение по замещению",
          "error",
        );
      }
    },
    [decideRDMutation, showToast],
  );

  const decideReg = useCallback(
    async (r, ok, reason = null) => {
      try {
        await decideRegMutation.mutateAsync({
          id: r.id,
          approved: ok,
          ...(reason !== null ? { reason } : {}),
        });
      } catch (err) {
        showToast(
          err.message || "Не удалось применить решение по регистрации",
          "error",
        );
      }
    },
    [decideRegMutation, showToast],
  );

  const revokeRD = useCallback(
    async (r) => {
      try {
        await revokeRDMutation.mutateAsync(r.id);
      } catch (err) {
        showToast(err.message || "Не удалось отозвать замещение", "error");
      }
    },
    [revokeRDMutation, showToast],
  );

  const forceActivateRD = useCallback(
    async (r) => {
      const ok = await confirm({
        message: "Применить замещение до согласия получателя?",
        confirmLabel: "Применить",
      });
      if (!ok) return;
      try {
        await activateRDMutation.mutateAsync(r.id);
      } catch (err) {
        showToast(err.message || "Не удалось применить замещение", "error");
      }
    },
    [activateRDMutation, confirm, showToast],
  );

  const openReject = useCallback(
    (kind, item) => setRejecting({ kind, item }),
    [],
  );

  const confirmReject = useCallback(
    async (reason) => {
      if (!rejecting) return;
      const { kind, item } = rejecting;
      if (CHANGE_KINDS[kind]) await decideChange(item, false, reason);
      else if (kind === "rd") await decideRD(item, false, reason);
      else if (kind === "reg") await decideReg(item, false, reason);
      else if (kind === "assignment") {
        try {
          await decideAssignmentMutation.mutateAsync({
            id: item.id,
            action: "reject",
            reason,
            __idempotencyKey: crypto.randomUUID(),
          });
        } catch (err) {
          showToast(err.message || "Не удалось отклонить заявку", "error");
        }
      }
      setRejecting(null);
    },
    [
      rejecting,
      decideChange,
      decideRD,
      decideReg,
      decideAssignmentMutation,
      showToast,
    ],
  );
  const closeReject = useCallback(() => setRejecting(null), []);

  const confirmReassign = useCallback(
    async (newAssigneeId) => {
      if (!reassignFor) return;
      try {
        await decideAssignmentMutation.mutateAsync({
          id: reassignFor.id,
          action: "reassign",
          newAssigneeId,
          __idempotencyKey: crypto.randomUUID(),
        });
        setReassignFor(null);
      } catch (err) {
        showToast(err.message || "Не удалось переназначить", "error");
      }
    },
    [reassignFor, decideAssignmentMutation, showToast],
  );
  const closeReassign = useCallback(() => setReassignFor(null), []);

  const rejectionTitle = useMemo(() => {
    if (!rejecting) return "Причина отклонения";
    if (CHANGE_KINDS[rejecting.kind])
      return `Причина отклонения: ${CHANGE_KINDS[rejecting.kind].label.toLowerCase()}`;
    switch (rejecting.kind) {
      case "rd":
        return "Причина отклонения замещения";
      case "reg":
        return "Причина отклонения регистрации";
      case "assignment":
        return "Причина отклонения назначения";
      default:
        return "Причина отклонения";
    }
  }, [rejecting]);

  const renderChangeKindTab = (kindId) => {
    const kind = CHANGE_KINDS[kindId];
    const items = changeRequests.filter((r) => r.changeKind === kindId);
    return (
      <div className="rep-panel">
        <div className="rep-panel-title">
          Запросы: {kind.label.toLowerCase()}
        </div>
        <table className="tbl">
          <thead>
            <tr>
              <th>Объект</th>
              <th>Текущее</th>
              <th>Предлагаемое</th>
              <th>Обоснование</th>
              <th>Запросил</th>
              <th>Решение</th>
            </tr>
          </thead>
          <tbody>
            {items.length === 0 && (
              <tr>
                <td colSpan="6" className="mut text-center">
                  Запросов нет
                </td>
              </tr>
            )}
            {items.map((r) => (
              <tr key={r.id}>
                <td>
                  <b>{targetTitleOf(r)}</b>
                </td>
                <td>{kind.formatValue(r.oldValue)}</td>
                <td>
                  <b>{kind.formatValue(r.newValue)}</b>
                </td>
                <td className="mut sm">{r.reason}</td>
                <td>{empName(r.reqId)}</td>
                <td>
                  {r.status === "pending" ? (
                    <>
                      <button
                        className="btn primary sm"
                        onClick={() => decideChange(r, true)}
                      >
                        Подтвердить
                      </button>
                      <button
                        className="btn danger sm"
                        onClick={() => openReject(kindId, r)}
                      >
                        Отклонить
                      </button>
                    </>
                  ) : (
                    <span
                      className={`st-chip ${r.status === "approved" ? "approved" : "rejected"}`}
                    >
                      {r.status}
                    </span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  const renderAssignmentsTab = () => {
    const mine = assignmentApprovals;
    return (
      <div className="rep-panel">
        <div className="rep-panel-title">Заявки на согласование назначения</div>
        <table className="tbl">
          <thead>
            <tr>
              <th>Объект</th>
              <th>Тип</th>
              <th>Исполнитель</th>
              <th>Запросил</th>
              <th>Дата</th>
              <th>Решение</th>
            </tr>
          </thead>
          <tbody>
            {mine.length === 0 && (
              <tr>
                <td colSpan="6" className="mut text-center">
                  Заявок нет
                </td>
              </tr>
            )}
            {mine.map((r) => {
              const kind = ASSIGNMENT_TARGET_KINDS[r.targetType];
              const status = ASSIGNMENT_APPROVAL_STATUSES[r.status];
              return (
                <tr key={r.id}>
                  <td>
                    <b>{targetTitleOf(r)}</b>
                  </td>
                  <td>{kind?.label || r.targetType}</td>
                  <td>{empName(r.assigneeId)}</td>
                  <td>{empName(r.requestedBy)}</td>
                  <td className="mut sm">{fmtDMY(r.createdAt)}</td>
                  <td>
                    {r.status === "pending" ? (
                      <>
                        <button
                          className="btn primary sm"
                          onClick={async () => {
                            const ok = await confirm({
                              message:
                                "Согласовать назначение? Задача станет активной.",
                              confirmLabel: "Согласовать",
                            });
                            if (!ok) return;
                            decideAssignmentMutation
                              .mutateAsync({
                                id: r.id,
                                action: "approve",
                                __idempotencyKey: crypto.randomUUID(),
                              })
                              .catch((err) => showToast(err.message, "error"));
                          }}
                        >
                          Согласовать
                        </button>{" "}
                        <button
                          className="btn ghost sm"
                          onClick={() => setReassignFor(r)}
                        >
                          Переназначить
                        </button>{" "}
                        <button
                          className="btn danger sm"
                          onClick={() => openReject("assignment", r)}
                        >
                          Отклонить
                        </button>
                      </>
                    ) : (
                      <span className={`st-chip ${r.status}`}>
                        {status?.label}
                      </span>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    );
  };

  const renderSubstitutionsTab = () => {
    const all = roleDelegations.filter(
      (rd) => rd.permissions?.canManage || rd.permissions?.canDecide,
    );
    return (
      <div className="rep-panel">
        <div className="rep-panel-title">Передача ролей и замещения</div>
        <table className="tbl">
          <thead>
            <tr>
              <th>От</th>
              <th>Кому</th>
              <th>Повод</th>
              <th>Период</th>
              <th>Роли</th>
              <th>Статус / действие</th>
            </tr>
          </thead>
          <tbody>
            {all.length === 0 && (
              <tr>
                <td colSpan="6" className="mut text-center">
                  Записей нет
                </td>
              </tr>
            )}
            {all.map((r) => {
              const isPending = r.status === "pending";
              const isActive = r.status === "active";
              const canDecide = isPending && Boolean(r.permissions?.canDecide);
              const canManage = Boolean(r.permissions?.canManage);
              const canForceActivate = isPending && canManage && !canDecide;
              const canRevoke = isActive && canManage;
              const isPastEnd = isActive && r.end && r.end < TODAY;
              const showApplyNow = canForceActivate && r.start <= TODAY;

              return (
                <tr key={r.id}>
                  <td>{empName(r.fromId)}</td>
                  <td>{empName(r.toId)}</td>
                  <td className="mut sm">{substitutionKindLabel(r.kind)}</td>
                  <td className="mut sm">
                    {fmtDMY(r.start)} - {r.end ? fmtDMY(r.end) : "до отмены"}
                  </td>
                  <td>
                    {(r.roles || [])
                      .map((x) => ROLES[x]?.label || x)
                      .join(", ")}
                  </td>
                  <td>
                    {isPending && (
                      <>
                        {canDecide && (
                          <>
                            <button
                              className="btn primary sm"
                              onClick={() => decideRD(r, true)}
                            >
                              Принять
                            </button>{" "}
                            <button
                              className="btn danger sm"
                              onClick={() => openReject("rd", r)}
                            >
                              Отклонить
                            </button>
                          </>
                        )}
                        {showApplyNow && (
                          <>
                            {canDecide && " "}
                            <button
                              className="btn primary sm"
                              onClick={() => forceActivateRD(r)}
                            >
                              Применить сейчас
                            </button>
                          </>
                        )}
                        {!canDecide && !canForceActivate && (
                          <span className="st-chip">
                            ожидает решения получателя
                          </span>
                        )}
                      </>
                    )}
                    {isActive && (
                      <>
                        <span className="st-chip approved">активно</span>
                        {isPastEnd && (
                          <>
                            {" "}
                            <span className="st-chip">истекло</span>
                          </>
                        )}
                        {canRevoke && (
                          <>
                            {" "}
                            <button
                              className="btn ghost sm"
                              onClick={() => revokeRD(r)}
                            >
                              {isPastEnd ? "Завершить" : "Отозвать"}
                            </button>
                          </>
                        )}
                      </>
                    )}
                    {r.status === "rejected" && (
                      <span className="st-chip rejected">отклонено</span>
                    )}
                    {r.status === "revoked" && (
                      <span className="st-chip revoked">отозвано</span>
                    )}
                    {r.status === "expired" && (
                      <span className="st-chip">истекло</span>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    );
  };

  return (
    <div>
      <div className="tabs">
        {tabs.map(([id, l]) => (
          <button
            key={id}
            className={"tab" + (tab === id ? " on" : "")}
            onClick={() => setTab(id)}
          >
            {l}
          </button>
        ))}
      </div>
      {canDecideChange && CHANGE_KINDS[tab] && renderChangeKindTab(tab)}
      {tab === "assignments" && renderAssignmentsTab()}
      {tab === "rd" && renderSubstitutionsTab()}
      {tab === "reg" && isAdmin && (
        <div className="rep-panel">
          <div className="rep-panel-title">Заявки на регистрацию</div>
          <table className="tbl">
            <thead>
              <tr>
                <th>Сотрудник</th>
                <th>E-mail</th>
                <th>Решение</th>
              </tr>
            </thead>
            <tbody>
              {registrations.map((r) => (
                <tr key={r.id}>
                  <td>
                    <b>
                      {r.last} {r.first}
                    </b>
                  </td>
                  <td>{r.email}</td>
                  <td>
                    {r.status === "pending" ? (
                      <>
                        <button
                          className="btn primary sm"
                          onClick={() => decideReg(r, true)}
                        >
                          Одобрить
                        </button>{" "}
                        <button
                          className="btn danger sm"
                          onClick={() => openReject("reg", r)}
                        >
                          Отклонить
                        </button>
                      </>
                    ) : (
                      <span
                        className={`st-chip ${r.status === "approved" ? "approved" : "rejected"}`}
                      >
                        {r.status}
                      </span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {rejecting && (
        <RejectionReasonModal
          title={rejectionTitle}
          onSubmit={confirmReject}
          onClose={closeReject}
        />
      )}
      {reassignFor && (
        <ReassignApprovalModal
          request={reassignFor}
          onConfirm={confirmReassign}
          onClose={closeReassign}
        />
      )}
    </div>
  );
}

export default Requests;
